import type { FloorGeometry } from './types'

export const BUILDING_A_NAME = 'Building A — Level 2'
export const BUILDING_A_WIDTH_M = 36
export const BUILDING_A_HEIGHT_M = 30

/** Percent positions for up to 5 APs on Building A Level 2. */
export const BUILDING_A_AP_POSITIONS = [
  { x: 17, y: 40, txPowerDbm: 18 },
  { x: 67, y: 40, txPowerDbm: 18 },
  { x: 50, y: 10, txPowerDbm: 16 },
  { x: 63, y: 75, txPowerDbm: 17 },
  { x: 25, y: 68, txPowerDbm: 15 },
] as const

export const BUILDING_A_GEOMETRY: FloorGeometry = {
  version: 1,
  heatmapLayer: 'rssi',
  bandGHz: 5,
  layers: {
    layout: true,
    rooms: false,
    walls: false,
    devices: true,
    clients: true,
    heatmap: true,
    alarms: true,
    cameras: true,
    sensors: true,
    topology: false,
  },
  rooms: [
    { id: 'mr1', name: 'Meeting Room 1', x: 0, y: 0, w: 16.67, h: 20 },
    { id: 'mr2', name: 'Meeting Room 2', x: 16.67, y: 0, w: 16.67, h: 20 },
    { id: 'conf', name: 'Conference Room', x: 33.33, y: 0, w: 33.33, h: 20 },
    { id: 'mgr', name: 'Manager Room', x: 66.67, y: 0, w: 16.67, h: 20 },
    { id: 'dir', name: 'Director Room', x: 83.33, y: 0, w: 16.67, h: 20 },
    { id: 'oo1', name: 'Open Office 1', x: 0, y: 20, w: 33.33, h: 40 },
    { id: 'oo2', name: 'Open Office 2', x: 50, y: 20, w: 33.33, h: 40 },
    { id: 'ter', name: 'Terrace', x: 83.33, y: 20, w: 16.67, h: 40 },
    { id: 'pan', name: 'Pantry', x: 0, y: 60, w: 16.67, h: 20 },
    { id: 'brk', name: 'Breakout Room', x: 16.67, y: 60, w: 16.67, h: 20 },
    { id: 'it', name: 'IT Room', x: 33.33, y: 67, w: 11.11, h: 20 },
    { id: 'stor', name: 'Storage', x: 44.44, y: 67, w: 11.11, h: 20 },
    { id: 'train', name: 'Training Room', x: 55.56, y: 60, w: 16.67, h: 30 },
  ],
  walls: [
    { id: 'w-ext-l', x1: 0, y1: 0, x2: 0, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-r', x1: 100, y1: 0, x2: 100, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-t', x1: 0, y1: 0, x2: 100, y2: 0, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-ext-b', x1: 0, y1: 100, x2: 100, y2: 100, material: 'concrete', heightM: 3, thicknessM: 0.3 },
    { id: 'w-row-top', x1: 0, y1: 20, x2: 100, y2: 20, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-row-mid', x1: 33.33, y1: 20, x2: 33.33, y2: 60, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-oo2-ter', x1: 83.33, y1: 20, x2: 83.33, y2: 60, material: 'glass', heightM: 2.7, thicknessM: 0.12 },
    { id: 'w-oo2-core', x1: 50, y1: 20, x2: 50, y2: 60, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-corridor', x1: 33.33, y1: 60, x2: 72, y2: 60, material: 'drywall', heightM: 2.7, thicknessM: 0.15 },
    { id: 'w-conf-glass', x1: 33.33, y1: 20, x2: 66.67, y2: 20, material: 'glass', heightM: 2.7, thicknessM: 0.12 },
    { id: 'w-it-l', x1: 33.33, y1: 67, x2: 33.33, y2: 87, material: 'brick', heightM: 2.7, thicknessM: 0.2 },
    { id: 'w-it-r', x1: 44.44, y1: 67, x2: 44.44, y2: 87, material: 'brick', heightM: 2.7, thicknessM: 0.2 },
  ],
  obstacles: [
    { id: 'obs-elev', type: 'elevator', x: 44, y: 38, w: 7, h: 10, attenuationDb: 28 },
    { id: 'obs-stair-w', type: 'metal_cabinet', x: 36, y: 32, w: 5, h: 8, attenuationDb: 15 },
    { id: 'obs-stair-e', type: 'metal_cabinet', x: 52, y: 32, w: 5, h: 8, attenuationDb: 15 },
    { id: 'obs-rack1', type: 'server_rack', x: 35, y: 78, w: 4, h: 5, attenuationDb: 18 },
    { id: 'obs-rack2', type: 'server_rack', x: 40, y: 78, w: 4, h: 5, attenuationDb: 18 },
    { id: 'obs-elec-w', type: 'metal_cabinet', x: 38, y: 48, w: 4, h: 4, attenuationDb: 12 },
    { id: 'obs-elec-e', type: 'metal_cabinet', x: 48, y: 48, w: 4, h: 4, attenuationDb: 12 },
  ],
  devices: [
    { id: 'dev-sw-idf', type: 'switch', name: 'IDF-SW-01', x: 38, y: 80, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-fw', type: 'firewall', name: 'IDF-FW-01', x: 41, y: 80, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-wlc', type: 'wireless_controller', name: 'WLC-01', x: 38, y: 76, status: 'healthy', heightM: 1.8, mount: 'rack' },
    { id: 'dev-cam-pan', type: 'camera', name: 'Pantry-Cam-01', x: 8, y: 68, status: 'healthy', heightM: 2.8, mount: 'wall' },
    { id: 'dev-cam-conf', type: 'camera', name: 'Conf-Cam-01', x: 50, y: 8, status: 'healthy', heightM: 2.8, mount: 'ceiling' },
    { id: 'dev-sensor', type: 'sensor', name: 'Env-Sensor-01', x: 63, y: 72, status: 'healthy', heightM: 2.4, mount: 'wall' },
    { id: 'dev-ups', type: 'ups', name: 'IT-UPS-01', x: 35, y: 84, status: 'healthy', heightM: 0.5, mount: 'floor' },
  ],
}

export function isBuildingADemoFloor(floorId: string, bgRaw: string): boolean {
  return (
    floorId === 'f0000000-0000-0000-0000-000000000001' ||
    (bgRaw.includes('building-a-level-2') && !bgRaw.includes('v2'))
  )
}

export function isBuildingALayoutStale(
  floorId: string,
  fp: { widthM?: unknown; heightM?: unknown; floorName?: unknown; backgroundImage?: unknown; geometry?: unknown },
  geo: FloorGeometry,
): boolean {
  if (!isBuildingADemoFloor(floorId, String(fp.backgroundImage ?? ''))) return false
  const wrongName = String(fp.floorName ?? '').includes('HQ Floor')
  const sparseGeometry = geo.walls.length < 8 || geo.devices.length < 4
  return wrongName || sparseGeometry
}
